import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CreditCard, Landmark, Lock, CheckCircle2, MessageCircle } from 'lucide-react';
import { CLINIC } from '../mock';
import { useLang } from '../context/LangContext';
import { SlotPicker, slotLabel } from './SlotPicker';
import { PackagePicker, PACKAGES } from './PackagePicker';
import { HistoryFields, EMPTY_HISTORY } from './HistoryFields';
import { ReportUpload } from './ReportUpload';
import { BankDetails, MethodPicker } from './BankDetails';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNTRIES = ['USA', 'United Kingdom', 'Australia', 'New Zealand', 'UAE', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Oman', 'Bahrain', 'Other'];
const EMPTY = { name: '', email: '', whatsapp: '', country: 'UAE', concern: '' };
const INPUT = 'w-full px-4 py-3 rounded-xl border border-teal-100 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition text-sm';

const loadCheckout = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = resolve;
  s.onerror = reject;
  document.body.appendChild(s);
});

function Paid({ booking, form, price, slot, reportCount }) {
  const { tr } = useLang();
  const top = useRef(null);
  useEffect(() => { top.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, []);
  const when = slotLabel(slot.date, slot.time);
  const msg = encodeURIComponent(`Hello, I have paid ${price} for my online video consultation on ${when} IST (booking ${booking.slice(0, 8)}). Name: ${form.name}, ${form.country}. ${reportCount ? `I have uploaded ${reportCount} report file(s) on the website.` : 'I am sending my reports here.'}`);
  return (
    <div ref={top} data-testid="intl-pay-success" className="text-center py-8 scroll-mt-28">
      <span className="grid place-items-center w-16 h-16 rounded-full bg-teal-50 text-teal-600 mx-auto"><CheckCircle2 size={34} /></span>
      <h3 className="font-display text-2xl text-teal-900 mt-4" style={{ fontWeight: 600 }}>{tr('Payment received, thank you!', 'भुगतान मिल गया, धन्यवाद!')}</h3>
      <p data-testid="intl-pay-slot-confirmed" className="inline-block mt-3 px-4 py-2 rounded-full bg-teal-50 text-teal-800 font-semibold text-sm">{tr('Your video consultation', 'आपका वीडियो परामर्श')}: {when} IST</p>
      <p className="text-slate-600 mt-2 max-w-md mx-auto">{tr('Dr Puneet will video-call you on WhatsApp at this time. Please send your reports on WhatsApp before the call.', 'डॉ. पुनीत इसी समय आपको व्हाट्सऐप पर वीडियो कॉल करेंगे। कृपया कॉल से पहले अपनी रिपोर्ट व्हाट्सऐप पर भेज दें।')}</p>
      <a data-testid="intl-pay-whatsapp" href={`${CLINIC.whatsapp}?text=${msg}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white mt-6" style={{ background: '#25D366' }}>
        <MessageCircle size={18} /> {tr('Send my reports on WhatsApp', 'व्हाट्सऐप पर रिपोर्ट भेजें')}
      </a>
    </div>
  );
}

export const IntlPayForm = ({ plan = 'intl' }) => {
  const { tr } = useLang();
  const india = plan === 'india';
  const [pkg, setPkg] = useState('initial');
  const [history, setHistory] = useState(EMPTY_HISTORY);
  const [reports, setReports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [method, setMethod] = useState('card');
  const [transfer, setTransfer] = useState(null);
  const wise = !india && method === 'wise';
  const pkgInfo = PACKAGES.find((p) => p.id === pkg);
  const price = india ? '\u20b91,500' : `USD ${pkgInfo.usd}`;
  const [form, setForm] = useState(india ? { ...EMPTY, country: 'India' } : EMPTY);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [booking, setBooking] = useState('');
  const [slot, setSlot] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pay = async (e) => {
    e.preventDefault();
    if (!slot) { setError(tr('Please choose a date and time.', 'कृपया तारीख़ और समय चुनें।')); return; }
    setStatus('loading');
    setError('');
    const body = { name: form.name, email: form.email, whatsapp: form.whatsapp, country: form.country, concern: form.concern, plan, slot_date: slot.date, slot_time: slot.time, ...(india ? {} : { package: pkg, history, report_ids: reports.map((r) => r.id) }) };
    try {
      if (wise) {
        const { data: t } = await axios.post(`${API}/intl-bookings/bank-transfer`, body);
        setTransfer(t);
        setStatus('transfer');
        return;
      }
      const { data } = await axios.post(`${API}/intl-bookings/order`, body);
      await loadCheckout();
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: 'Srigyan Rheumatology Clinic', description: india ? 'Online video consultation' : pkgInfo.name,
        prefill: { name: form.name, email: form.email, contact: form.whatsapp },
        theme: { color: '#0f766e' },
        handler: async (res) => {
          try {
            await axios.post(`${API}/intl-bookings/verify`, { booking_id: data.booking_id, ...res });
            setBooking(data.booking_id);
            setStatus('paid');
          } catch {
            setError(tr('We could not confirm your payment. Please contact us on WhatsApp.', 'भुगतान की पुष्टि नहीं हो सकी। कृपया व्हाट्सऐप पर संपर्क करें।'));
            setStatus('idle');
          }
        },
        modal: { ondismiss: () => setStatus('idle') },
      });
      rzp.on('payment.failed', () => { setError(tr('Payment failed. Please try another card.', 'भुगतान विफल रहा। कृपया दूसरा कार्ड आज़माएँ।')); setStatus('idle'); });
      rzp.open();
    } catch (err) {
      if (err.response?.status === 409) { setSlot(null); setRefresh((n) => n + 1); }
      setError(err.response?.status === 409
        ? tr('That time was just taken. Please choose another slot.', 'यह समय अभी-अभी बुक हो गया। कृपया दूसरा समय चुनें।')
        : err.response?.status === 503
        ? tr('Online payment is not available yet. Please book on WhatsApp.', 'ऑनलाइन भुगतान अभी उपलब्ध नहीं है। कृपया व्हाट्सऐप पर बुक करें।')
        : err.response?.data?.detail && typeof err.response.data.detail === 'string' ? err.response.data.detail : tr('Something went wrong. Please book on WhatsApp.', 'कुछ गड़बड़ हो गई। कृपया व्हाट्सऐप पर बुक करें।'));
      setStatus('idle');
    }
  };

  if (status === 'transfer') return <BankDetails data={transfer} slot={slot} form={form} />;
  if (status === 'paid') return <Paid booking={booking} form={form} price={price} slot={slot} reportCount={reports.length} />;
  return (
    <form data-testid={india ? 'india-pay-form' : 'intl-pay-form'} onSubmit={pay} className="space-y-4 min-w-0">
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="intl-pay-name" required value={form.name} onChange={set('name')} placeholder={tr('Full name', 'पूरा नाम')} className={INPUT} />
        <input data-testid="intl-pay-email" required type="email" value={form.email} onChange={set('email')} placeholder={tr('Email', 'ईमेल')} className={INPUT} />
        <input data-testid="intl-pay-whatsapp" required value={form.whatsapp} onChange={set('whatsapp')} placeholder={india ? tr('WhatsApp number', 'व्हाट्सऐप नंबर') : tr('WhatsApp number with country code', 'कंट्री कोड के साथ व्हाट्सऐप नंबर')} className={INPUT} />
        {india ? (
          <input data-testid="intl-pay-city" value={form.concern_city || ''} onChange={(e) => setForm({ ...form, concern_city: e.target.value, country: e.target.value ? `India (${e.target.value})` : 'India' })} placeholder={tr('Your city', 'आपका शहर')} className={INPUT} />
        ) : (
          <select data-testid="intl-pay-country" value={form.country} onChange={set('country')} className={`${INPUT} bg-white`}>
            {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        )}
      </div>
      {!india && <PackagePicker value={pkg} onChange={setPkg} />}
      <SlotPicker key={refresh} value={slot} onChange={(v) => { setSlot(v); setError(''); }} showLocal={!india} />
      <textarea data-testid="intl-pay-concern" rows={3} value={form.concern} onChange={set('concern')} placeholder={tr('Briefly, what would you like help with? (optional)', 'संक्षेप में, आपको किस बारे में मदद चाहिए? (वैकल्पिक)')} className={`${INPUT} resize-none`} />
      {!india && <HistoryFields value={history} onChange={setHistory} />}
      {!india && <ReportUpload files={reports} onChange={setReports} onBusy={setUploading} />}
      {!india && <MethodPicker value={method} onChange={setMethod} />}
      {error && (
        <div data-testid="intl-pay-error" className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-red-50 px-4 py-3">
          <p className="text-sm" style={{ color: 'var(--coral-dark)' }}>{error}</p>
          <a data-testid="intl-pay-error-whatsapp" href={`${CLINIC.whatsapp}?text=${encodeURIComponent(`Hello, I would like to book an online video consultation${slot ? ` on ${slotLabel(slot.date, slot.time)} IST` : ''}. Name: ${form.name}, ${form.country}.`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-sm font-semibold" style={{ background: '#25D366' }}><MessageCircle size={15} /> WhatsApp</a>
        </div>
      )}
      <button data-testid="intl-pay-submit" type="submit" disabled={status === 'loading' || uploading} className="btn-coral w-full py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70">
        {wise ? <Landmark size={18} /> : <CreditCard size={18} />} {status === 'loading' && wise ? 'Holding your slot…' : wise ? `Hold my slot & get bank details (${price})` : status === 'loading' ? tr('Opening secure payment…', 'सुरक्षित भुगतान खुल रहा है…') : india ? tr('Pay ₹1,500 (UPI / card)', '₹1,500 भुगतान करें (UPI / कार्ड)') : `Pay ${price} by card`}
      </button>
      <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400"><Lock size={13} /> {wise ? 'Bank details for our Wise account are shown after your slot is held.' : india ? tr('Secure payment by Razorpay: UPI, cards, netbanking and wallets.', 'Razorpay द्वारा सुरक्षित भुगतान: UPI, कार्ड, नेटबैंकिंग और वॉलेट।') : tr('Secure payment by Razorpay. Visa, Mastercard and other international cards.', 'Razorpay द्वारा सुरक्षित भुगतान। Visa, Mastercard और अन्य अंतरराष्ट्रीय कार्ड।')}</p>
    </form>
  );
};
